import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  transcriptionsApi,
  MeetingTranscriptionItem,
  meetingPromptsApi,
  MeetingPromptsDto,
} from '../services/api';

interface TranscriptSegment {
  id: string;
  start: number;
  end: number;
  channel: number;
  speaker_id: string;
  transcript: string;
}

interface TranscriptManifestChunk {
  index: number;
  start: number;
  end: number;
  path: string;
  segment_count: number;
}

interface TranscriptDetails {
  id: string;
  sessionId: string;
  status: string;
  title?: string | null;
  createdAt: string;
  updatedAt: string;
  finalTranscript?: string;
  finalTranscriptSegments?: TranscriptSegment[];
  transcriptManifest?: {
    version: number;
    transcript_id: string;
    created_at: string;
    duration_sec: number;
    segment_chunks: TranscriptManifestChunk[];
  };
  transcriptResultsSummarized?: string;
  rawTranscriptAvailable?: boolean;
  isPostProcessing?: boolean;
}

interface SpeakerMapItem {
  id: string;
  speakerKey: string;
  label: string;
  color?: string | null;
}

interface TranscriptViewProps {
  transcript: MeetingTranscriptionItem | null;
  /** Incremented by parent when this transcript changes via WebSocket, to trigger a refresh. */
  version?: number;
  onTranscriptUpdate?: (transcript: MeetingTranscriptionItem) => void;
}

const SCROLL_LOAD_THRESHOLD = 200;
const MAX_INITIAL_AUTO_CHUNKS = 3;

// Simple repeating pattern to fake an audio waveform.
const WAVEFORM_BAR_HEIGHTS = [6, 12, 18, 10, 22, 28, 14, 20, 26, 16, 30, 12, 24, 8, 18, 22];

function formatTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds)) return '';
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const two = (n: number) => n.toString().padStart(2, '0');
  return hours > 0
    ? `${two(hours)}:${two(minutes)}:${two(secs)}`
    : `${two(minutes)}:${two(secs)}`;
}

const SPEAKER_PALETTE = [
  '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e',
  '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#14b8a6',
  '#06b6d4', '#0ea5e9', '#3b82f6',
];

function getColorForSpeakerFallback(str: string): string {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return SPEAKER_PALETTE[Math.abs(h) % SPEAKER_PALETTE.length];
}

function getSpeakerDisplayColor(speaker: SpeakerMapItem): string {
  if (speaker.color) return speaker.color;
  return getColorForSpeakerFallback(speaker.label || speaker.speakerKey);
}

const TranscriptView: React.FC<TranscriptViewProps> = ({ transcript, version = 0, onTranscriptUpdate }) => {
  const [transcriptDetails, setTranscriptDetails] = useState<TranscriptDetails | null>(null);
  const [speakers, setSpeakers] = useState<SpeakerMapItem[]>([]);
  const [displayedSegments, setDisplayedSegments] = useState<TranscriptSegment[]>([]);
  const [loadedChunkCount, setLoadedChunkCount] = useState(0);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState('');
  const [editingSpeakerId, setEditingSpeakerId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [meetingPrompts, setMeetingPrompts] = useState<MeetingPromptsDto | null>(null);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const [waveBarCount, setWaveBarCount] = useState(80);

  const loadTranscriptDetails = async () => {
    if (!transcript) return;

    try {
      setLoading(true);
      setDisplayedSegments([]);
      setLoadedChunkCount(0);
      const [details, speakersList, prompts] = await Promise.all([
        transcriptionsApi.getOne(transcript.id),
        transcriptionsApi.listSpeakers(transcript.id).catch(() => []),
        meetingPromptsApi.getMyPrompts().catch(() => null),
      ]);
      const d = details as TranscriptDetails;
      setTranscriptDetails(d);
      setSpeakers(speakersList);
      if (prompts) {
        setMeetingPrompts(prompts);
        setSelectedTemplateIndex(
          prompts.currentTemplateIndex >= 0 &&
            prompts.currentTemplateIndex < prompts.templates.length
            ? prompts.currentTemplateIndex
            : 0,
        );
      }
      if (d.transcriptManifest?.segment_chunks?.length) {
        setLoadingChunk(true);
        try {
          const chunk0 = await transcriptionsApi.getTranscriptChunk(transcript.id, 0);
          setDisplayedSegments(chunk0.segments ?? []);
          setLoadedChunkCount(1);
        } catch (err) {
          console.error('Failed to load first transcript chunk:', err);
        } finally {
          setLoadingChunk(false);
        }
      }
    } catch (error) {
      console.error('Failed to load transcript details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNextChunk = useCallback(async () => {
    if (!transcript?.id || !transcriptDetails?.transcriptManifest) return;
    const chunks = transcriptDetails.transcriptManifest.segment_chunks;
    if (loadedChunkCount >= chunks.length || loadingChunk) return;
    setLoadingChunk(true);
    try {
      const chunk = await transcriptionsApi.getTranscriptChunk(transcript.id, loadedChunkCount);
      setDisplayedSegments((prev) => [...prev, ...(chunk.segments ?? [])]);
      setLoadedChunkCount((c) => c + 1);
    } catch (err) {
      console.error('Failed to load transcript chunk:', err);
    } finally {
      setLoadingChunk(false);
    }
  }, [transcript?.id, transcriptDetails?.transcriptManifest, loadedChunkCount, loadingChunk]);

  // Ensure that on very tall viewports we still load enough chunks so the transcript
  // area can scroll, otherwise the "Scroll for more" hint is shown but no scrollbars appear.
  useEffect(() => {
    if (!transcriptDetails?.transcriptManifest) return;
    const el = transcriptScrollRef.current;
    if (!el) return;
    const totalChunks = transcriptDetails.transcriptManifest.segment_chunks.length;
    // Only auto-prefetch a limited number of chunks to avoid loading "everything" on huge transcripts.
    const maxAuto = Math.min(totalChunks, MAX_INITIAL_AUTO_CHUNKS);
    if (loadedChunkCount >= maxAuto) return;
    // If content height is not significantly larger than the viewport, prefetch next chunk.
    if (el.scrollHeight <= el.clientHeight + SCROLL_LOAD_THRESHOLD) {
      loadNextChunk();
    }
  }, [transcriptDetails?.transcriptManifest, loadedChunkCount, loadNextChunk]);

  const handleTranscriptScroll = useCallback(() => {
    const el = transcriptScrollRef.current;
    if (!el || !transcriptDetails?.transcriptManifest) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_LOAD_THRESHOLD) {
      loadNextChunk();
    }
  }, [transcriptDetails?.transcriptManifest, loadNextChunk]);

  const handleSpeakerLabelSave = useCallback(
    async (speakerId: string) => {
      if (!transcript?.id || editingLabel.trim() === '') {
        setEditingSpeakerId(null);
        return;
      }
      try {
        await transcriptionsApi.updateSpeaker(transcript.id, speakerId, { label: editingLabel.trim() });
        const list = await transcriptionsApi.listSpeakers(transcript.id);
        setSpeakers(list);
      } catch (err) {
        console.error('Failed to update speaker label:', err);
      }
      setEditingSpeakerId(null);
    },
    [transcript?.id, editingLabel],
  );

  const handleGenerateSummary = useCallback(async () => {
    if (!transcript?.id || generatingSummary) return;
    try {
      setGeneratingSummary(true);
      await transcriptionsApi.generateSummary(transcript.id, selectedTemplateIndex);
      // Immediately refresh details; polling effect will keep it up to date while in progress.
      const latest = await transcriptionsApi.getOne(transcript.id);
      setTranscriptDetails(latest as TranscriptDetails);
    } catch (err) {
      console.error('Failed to generate summary:', err);
      alert('Failed to generate summary. Please try again.');
    } finally {
      setGeneratingSummary(false);
    }
  }, [transcript?.id, selectedTemplateIndex, generatingSummary]);

  // Resize-aware bar count so the fake waveform fills its container horizontally.
  useEffect(() => {
    const updateWaveBars = () => {
      if (!waveformRef.current) return;
      const width = waveformRef.current.clientWidth;
      if (!width) return;
      // Approximate 3px bar + 1px gap = 4px per bar.
      const target = Math.max(40, Math.ceil(width / 4));
      setWaveBarCount(target);
    };
    updateWaveBars();
    window.addEventListener('resize', updateWaveBars);
    return () => {
      window.removeEventListener('resize', updateWaveBars);
    };
  }, []);

  // Initial/full load when a different transcript is selected.
  useEffect(() => {
    if (transcript) {
      loadTranscriptDetails();
    } else {
      setTranscriptDetails(null);
      setDisplayedSegments([]);
      setLoadedChunkCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript?.id]);

  // Lightweight summary refresh when the selected transcript is updated via WebSocket (version bump).
  useEffect(() => {
    if (!transcript?.id || version === 0) return;
    const refreshSummaryOnly = async () => {
      try {
        const latest = await transcriptionsApi.getOne(transcript.id);
        const latestDetails = latest as TranscriptDetails;
        setTranscriptDetails((prev) => {
          if (!prev) return latestDetails;
          return {
            ...prev,
            title: latestDetails.title,
            transcriptResultsSummarized: latestDetails.transcriptResultsSummarized,
            // isPostProcessing might be undefined in older responses, default to false
            isPostProcessing: (latestDetails as any).isPostProcessing ?? false,
            updatedAt: latestDetails.updatedAt,
          };
        });
      } catch (err) {
        console.error('Failed to refresh summary details:', err);
      }
    };
    refreshSummaryOnly();
  }, [version, transcript?.id]);

  const handleDownload = async () => {
    if (!transcript) return;
    
    try {
      setDownloading(true);
      await transcriptionsApi.downloadTranscript(transcript.id);
    } catch (error: any) {
      console.error('Failed to download transcript:', error);
      alert(error.response?.data?.message || 'Failed to download transcript.');
    } finally {
      setDownloading(false);
    }
  };

  if (!transcript) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-gray-500 text-lg">Select a transcript to view</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-gray-500 text-lg">Loading transcript...</div>
      </div>
    );
  }

  const manifest = transcriptDetails?.transcriptManifest;
  const finalTranscript = transcriptDetails?.finalTranscript || '';
  const finalTranscriptSegments = transcriptDetails?.finalTranscriptSegments ?? [];
  const summary = transcriptDetails?.transcriptResultsSummarized || '';
  const isPostProcessing = transcriptDetails?.isPostProcessing ?? false;
  const isCompleted = transcript.status === 'completed';
  const labelBySpeakerId: Record<string, string> = {};
  const speakerByKey: Record<string, SpeakerMapItem> = {};
  speakers.forEach((s) => {
    labelBySpeakerId[s.speakerKey] = s.label;
    speakerByKey[s.speakerKey] = s;
  });
  const segmentsToShow = manifest ? displayedSegments : finalTranscriptSegments;
  const transcriptDisplayText =
    segmentsToShow.length > 0
      ? segmentsToShow
          .map((seg) => `${labelBySpeakerId[seg.speaker_id] ?? seg.speaker_id}: ${seg.transcript}`)
          .join('\n')
      : finalTranscript;
  const hasMoreChunks = manifest && loadedChunkCount < (manifest.segment_chunks?.length ?? 0);
  const getSegmentPillColor = (speakerId: string): string => {
    const speaker = speakerByKey[speakerId];
    return speaker ? getSpeakerDisplayColor(speaker) : getColorForSpeakerFallback(speakerId);
  };

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-800 truncate">
              {transcript.title || 'Untitled meeting'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {new Date(transcript.createdAt).toLocaleString()}
            </p>
            {isCompleted && speakers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 min-w-0 max-w-full">
                {speakers.map((speaker) => {
                  const bg = getSpeakerDisplayColor(speaker);
                  const isEditing = editingSpeakerId === speaker.id;
                  return (
                    <div key={speaker.id} className="flex items-center">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingLabel}
                          onChange={(e) => setEditingLabel(e.target.value)}
                          onBlur={() => handleSpeakerLabelSave(speaker.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSpeakerLabelSave(speaker.id);
                            }
                            if (e.key === 'Escape') {
                              setEditingSpeakerId(null);
                            }
                          }}
                          className="rounded-full px-3 py-1 text-sm border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-32"
                          autoFocus
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSpeakerId(speaker.id);
                            setEditingLabel(speaker.label);
                          }}
                          className="rounded-full px-3 py-1 text-sm font-medium text-white border-0 cursor-pointer hover:opacity-90"
                          style={{ backgroundColor: bg }}
                        >
                          {speaker.label}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {isCompleted && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className={`ml-4 px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 ${
                downloading ? 'cursor-not-allowed' : ''
              }`}
            >
              {downloading ? 'Downloading...' : 'Download'}
            </button>
          )}
        </div>
      </div>

      {/* Content: two scrollable panels (Transcript ~70%, Summary ~30%) */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Transcript panel (header fixed, content scrollable) */}
        <div className="flex-1 basis-2/3 min-h-0 flex flex-col border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-2">
            <h3 className="text-lg font-semibold text-gray-800">Transcript</h3>
          </div>
          <div
            ref={transcriptScrollRef}
            onScroll={handleTranscriptScroll}
            className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-4"
          >
            {isCompleted && transcriptDisplayText ? (
              <>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                {segmentsToShow.length > 0 ? (
                  <div className="space-y-3">
                    {segmentsToShow.map((seg) => {
                      const label = labelBySpeakerId[seg.speaker_id] ?? seg.speaker_id;
                      const pillColor = getSegmentPillColor(seg.speaker_id);
                      const ts = formatTimestamp(seg.start);
                      return (
                        <div key={seg.id} className="space-y-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span
                              className="rounded-full px-2.5 py-0.5 text-xs font-medium text-white shrink-0"
                              style={{ backgroundColor: pillColor }}
                            >
                              {label}
                            </span>
                            <span className="font-mono text-xs text-gray-500">
                              {ts}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700">
                            {seg.transcript}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                    {transcriptDisplayText}
                  </pre>
                )}
                </div>
                {hasMoreChunks && loadingChunk && (
                  <div className="mt-3 text-center py-2 text-sm text-gray-500">
                    Loading more...
                  </div>
                )}
              </>
            ) : !isCompleted ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  {transcript.status === 'processing' || transcript.status === 'uploading_done'
                    ? 'Transcript is being processed. Please check back later.'
                    : transcript.status === 'failed'
                      ? 'Transcript processing failed.'
                      : transcript.status === 'cancelled'
                        ? 'Transcript processing was cancelled.'
                        : 'Transcript is not yet available.'}
                </p>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  Transcript content is not yet available.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Summary panel (header fixed, content scrollable) */}
        <div className="flex-1 basis-1/3 min-h-0 flex flex-col">
          <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-2 border-t border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {isPostProcessing ? 'Summary (in progress...)' : 'Summary'}
              </h3>
              {meetingPrompts && (
                <div className="flex items-center gap-2">
                  <select
                    className="border border-gray-300 rounded-md text-sm px-2 py-1 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                    value={selectedTemplateIndex}
                    onChange={(e) => setSelectedTemplateIndex(Number(e.target.value))}
                    disabled={isPostProcessing || generatingSummary}
                  >
                    {meetingPrompts.templates.map((tpl, idx) => (
                      <option key={idx} value={idx}>
                        {tpl.title}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleGenerateSummary}
                    disabled={isPostProcessing || generatingSummary}
                    className="inline-flex items-center px-3 py-1.5 border border-indigo-600 text-xs font-medium rounded-md text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingSummary || isPostProcessing ? 'Generating…' : 'Generate summary'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 lg:p-8">
            {isCompleted && summary ? (
              <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {summary}
                </p>
              </div>
            ) : isPostProcessing ? (
              <p className="text-sm text-gray-500">
                Generating summary with AI…
              </p>
            ) : (
              <p className="text-sm text-gray-500">No summary available.</p>
            )}
          </div>
        </div>
      </div>

      {/* Audio waveform teaser (premium feature) */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white">
        <div className="max-w-full bg-gray-900 rounded-lg px-4 py-3 flex items-center gap-4 relative overflow-hidden">
          {/* Fake play button */}
          <button
            type="button"
            className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-500 text-white shadow-md cursor-default"
            aria-label="Play recording (premium feature)"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path
                d="M8 5v14l11-7z"
                fill="currentColor"
              />
            </svg>
          </button>

          {/* Fake waveform */}
          <div className="flex-1 h-14 relative" ref={waveformRef}>
            <div className="absolute inset-0 flex items-center gap-[2px] opacity-80">
              {Array.from({ length: waveBarCount }).map((_, idx) => {
                const h = WAVEFORM_BAR_HEIGHTS[idx % WAVEFORM_BAR_HEIGHTS.length];
                return (
                  <div
                    key={idx}
                    className="w-[3px] rounded-full bg-indigo-300"
                    style={{ height: `${h}px` }}
                  />
                );
              })}
            </div>
            {/* Lock overlay with light tint so waveform remains visible */}
            <div className="absolute inset-0 bg-gray-900/35 flex items-center justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 text-xs font-medium text-gray-100 border border-white/10">
                <span className="text-yellow-300">🔒</span>
                <span>Upgrade to Premium plan to access</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Input (Disabled) */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-end gap-2">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={true}
            placeholder="Chat with transcript (coming soon)..."
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
            rows={2}
          />
          <button
            disabled={true}
            className="px-6 py-3 bg-gray-400 text-white rounded-lg font-semibold cursor-not-allowed opacity-50"
          >
            Send
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Chat functionality will be available soon
        </p>
      </div>
    </div>
  );
};

export default TranscriptView;
